import { useMemo } from 'react';
import ReactFlow, {
  Background,
  Controls,
  Edge,
  MiniMap,
  Node,
  NodeTypes,
} from 'react-flow-renderer';
import { Flex } from '@chakra-ui/react';
import dagre from 'dagre';

import { colors, zIndexMap } from '@/styles/ui.theme';

import { CustomNode } from './custom-node';

const pedagogicalNodes = [
  { id: 'course', data: { label: 'Curso' } },
  { id: 'curriculumPath', data: { label: 'Matriz Curricular' } },
  { id: 'moduleBlock', data: { label: 'Bloco' } },
  { id: 'module', data: { label: 'Modulo' } },
  { id: 'submodule', data: { label: 'Submodulo' } },
  { id: 'discipline', data: { label: 'Disciplina' } },
  { id: 'disciplineInstance', data: { label: 'Versão de Disciplina' } },
  { id: 'lesson', data: { label: 'Aula' } },
  { id: 'reviewList', data: { label: 'Lista de Revisão' } },
  { id: 'video', data: { label: 'Video' } },
  { id: 'article', data: { label: 'Artigo' } },
  { id: 'exercise', data: { label: 'Exercicio' } },
  { id: 'objectiveExercise', data: { label: 'Exercicio Objetivo' } },
  { id: 'pensarResponder', data: { label: 'Pensar & Responder' } },
  { id: 'atividadePratica', data: { label: 'Atividade Prática' } },
];

const pedagogicalRelationships = [
  { id: 'course-curriculumPath', source: 'course', target: 'curriculumPath' },
  { id: 'curriculumPath-moduleBlock', source: 'curriculumPath', target: 'moduleBlock' },
  { id: 'moduleBlock-module', source: 'moduleBlock', target: 'module' },
  { id: 'module-submodule', source: 'module', target: 'submodule' },
  { id: 'submodule-discipline', source: 'submodule', target: 'discipline' },
  { id: 'discipline-disciplineInstance', source: 'discipline', target: 'disciplineInstance' },
  { id: 'disciplineInstance-lesson', source: 'disciplineInstance', target: 'lesson' },
  { id: 'disciplineInstance-reviewList', source: 'disciplineInstance', target: 'reviewList' },
  { id: 'lesson-video', source: 'lesson', target: 'video' },
  { id: 'lesson-article', source: 'lesson', target: 'article' },
  { id: 'lesson-exercise', source: 'lesson', target: 'exercise' },
  { id: 'exercise-objectiveExercise', source: 'exercise', target: 'objectiveExercise' },
  { id: 'exercise-pensarResponder', source: 'exercise', target: 'pensarResponder' },
  { id: 'exercise-atividadePratica', source: 'exercise', target: 'atividadePratica' },
];

export const ENTITTY_NAME = {
  WINDOW: 'Janela',
  COURSE: 'Curso',
  PATH: 'Matriz Curricular',
  BLOCK: 'Bloco',
  MODULE: 'Módulo',
  SUBMODULE: 'Submódulo',
  DISCIPLINE: 'Disciplina',
  DISCIPLINE_INSTANCE: 'Versão de Disciplina',
  LESSON: 'Aula',
  VIDEO: 'Vídeo',
  EXERCISE: 'Exercício',
  LIVE: 'Vídeo Ao Vivo',
  ARTICLE: 'Artigo',
  REVIEW_LIST: 'Lista de Revisão',
};

export type NodeWithoutPosition = Omit<Node, 'position'>;

const dagreGraph = new dagre.graphlib.Graph();
dagreGraph.setDefaultEdgeLabel(() => ({}));

// setting max node component dimensions to 240p
export const nodeWidth = 426;
export const nodeHeight = 240;

type LayoutedElements = {
  nodes: Node[];
  edges: Edge[];
};

const getLayoutedElements = (
  nodes: NodeWithoutPosition[],
  edges: Edge[],
  direction = 'TB',
): LayoutedElements => {
  const isHorizontal = direction === 'LR';
  dagreGraph.setGraph({ rankdir: direction });

  // eslint-disable-next-line unicorn/no-array-for-each
  nodes.forEach((node) => {
    dagreGraph.setNode(node.id, { width: nodeWidth, height: nodeHeight });
  });

  // eslint-disable-next-line unicorn/no-array-for-each
  edges.forEach((edge) => {
    dagreGraph.setEdge(edge.source, edge.target);
  });

  dagre.layout(dagreGraph);

  const positionedNodes: any = [...nodes];

  // eslint-disable-next-line unicorn/no-array-for-each
  positionedNodes.forEach((node: any) => {
    const nodeWithPosition = dagreGraph.node(node.id);
    node.targetPosition = isHorizontal ? 'left' : 'top';
    node.sourcePosition = isHorizontal ? 'right' : 'bottom';

    // We are shifting the dagre node position (anchor=center center) to the top left
    // so it matches the React Flow node anchor point (top left).
    node.position = {
      x: nodeWithPosition.x - nodeWidth / 2,
      y: nodeWithPosition.y - nodeHeight / 2,
    };

    return node;
  });

  return { nodes: positionedNodes, edges };
};

type Props = {
  nodes?: NodeWithoutPosition[];
  relationships?: Edge[];
  width: string;
  height: string;
  direction?: string;
};

const nodeColor = (node: Node): string => {
  switch (node.data.superHeading) {
    case ENTITTY_NAME.COURSE:
      return colors.descomplica.green.secondary;
    case ENTITTY_NAME.PATH:
      return colors.descomplica.secondary['secondary-purple-light'];
    case ENTITTY_NAME.BLOCK:
      return colors.descomplica.green.dark;
    case ENTITTY_NAME.MODULE:
      return colors.descomplica.secondary['secondary-blue-light'];
    case ENTITTY_NAME.SUBMODULE:
      return colors.descomplica.green.primary;
    case ENTITTY_NAME.DISCIPLINE:
      return colors.descomplica.blackboard.primary;
    default:
      return colors.descomplica.blackboard.primary;
  }
};

const CourseStructure = ({
  nodes,
  relationships,
  width,
  height,
  direction = 'TB',
}: Props): React.ReactElement => {
  // this memoization is crucial to maintain React Flow's performance
  // ref: https://reactflow.dev/docs/guides/custom-nodes/#adding-the-node-type
  const nodeTypes: NodeTypes | any = useMemo(() => ({ courseTreeNodeType: CustomNode }), []);

  const { nodes: layoutedNodes, edges: layoutedEdges } = getLayoutedElements(
    nodes || pedagogicalNodes,
    relationships || pedagogicalRelationships,
    direction,
  );

  return (
    <Flex
      w={width}
      h={height}
      zIndex={zIndexMap.hill}
      border='1px'
      borderColor={colors.descomplica.gray['secondary-gray-30']}
    >
      <ReactFlow nodes={layoutedNodes} edges={layoutedEdges} nodeTypes={nodeTypes} fitView>
        <Background />
        <MiniMap nodeColor={nodeColor} nodeStrokeWidth={3} />
        <Controls />
      </ReactFlow>
    </Flex>
  );
};

export default CourseStructure;
